/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BaseComponent } from '../../base.component';
import { Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';
import { LanguageService, UtilsService } from '../../../services';
import { nameValidation } from '../../../directives';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-rbac-group',
    templateUrl: './rbac-group.component.html',
    styleUrls: ['./rbac-group.component.scss']
})
export class RbacGroupComponent extends BaseComponent {
    /**
     * Get all the steps from the view, so we can scroll to the active step.
     */
    @ViewChildren('step') public steps: QueryList<any> = new QueryList<any>();

    /**
     * Incoming data from parent component.
     */
    @Input() set data(data: { users: any, roles: any, groups: any, groupPatch?: any }) {
        this._data = data;
        this.createFormGroups(this.data);
        this.editId = data.groupPatch?.id ? data.groupPatch.id : null;

        if (this.editId) {
            this.patchValuesToForm(data.groupPatch);
        }
    }

    /**
     * Tell parent to submit.
     */
    @Output() public submitGroup: EventEmitter<any> = new EventEmitter();

    /**
     * Give user option to expand all accordions.
     */
    public expandAll: boolean = false;

    /**
     * Force close the accordions.
     */
    public forceCollapseAll: boolean = false;

    /**
     * Enable/Disable the toggling of the accordions.
     */
    public enableToggleOfAccordions: boolean = true;

    /**
     * Store if we are editing or not.
     */
    public editId: any = null;

    /**
     * Store the current step.
     */
    public activeStep: number = 1;

    /**
     * The main formGroup variable.
     */
    public formGroup: FormGroup;

    /**
     * Stored endpoints.
     */
    public endpoints: any[] = [];

    /**
     * Grouping of users
     */
    public grouping: any[] = [
        { letters: ['a', 'b', 'c', 'd', 'e'], friendlyName: 'A - E' },
        { letters: ['f', 'g', 'h', 'i', 'j', 'k', 'l'], friendlyName: 'F - L' },
        { letters: ['m', 'n', 'o', 'p', 'q', 'r', 's'], friendlyName: 'M - S' },
        { letters: ['t', 'u', 'v', 'w', 'x', 'y', 'z'], friendlyName: 'T - Z' },
    ];

    /**
     * Filtered form groups.
     */
    public filteredFormGroups: object = {};

    /**
     * Store the getter and setter values.
     * @private
     */
    private _data: any;

    /**
     * To store the initial IP form data.
     * @private
     */
    private initialFormData: object = {};

    constructor(
        private formBuilder: FormBuilder,
        private languageService: LanguageService,
        private utilsService: UtilsService,
    ) {
        super();

        this.setupFormGroup();
        this.setUpFormSubscribers();
    }

    get data() {
        return this._data;
    }

    /**
     * Getter for subjectAlts in the Create CSR form.
     */
    get formRoles(): FormArray {
        return this.formGroup.get('step2.roles') as FormArray;
    }

    /**
     * Getter for subjectAlts in the Create CSR form.
     */
    get formUsers(): FormArray {
        return this.formGroup.get('step3.users') as FormArray;
    }

    /**
     * Toggle accordions.
     */
    public toggleAccordions(): void {
        this.expandAll = !this.expandAll;
    }

    /**
     * Go back a step in the process.
     */
    public backStep(): void {
        this.activeStep--;
        this.scrollToActiveStep();
        this.setAccordion(this.activeStep);
    }

    /**
     * Go forward a step in the process.
     */
    public nextStep(): void {
        this.activeStep++;
        this.scrollToActiveStep();
        this.setAccordion(this.activeStep);
    }

    /**
     * Go to a specific step in the process.
     * @param number
     */
    public setStep(number: number): void {
        this.activeStep = number;
        this.scrollToActiveStep();
        this.setAccordion(this.activeStep);
    }

    /**
     * Set back button disabled state.
     */
    public isBackButtonDisabled(): boolean {
        return this.activeStep === 1;
    }

    /**
     * Check if the button is disabled based on the form step state.
     */
    public isNextButtonDisabled(): boolean {
        if (this.activeStep === 2 || this.activeStep === 3) {
            return false;
        }

        return this.formGroup.get('step' + this.activeStep).invalid || this.formGroup.get('step' + this.activeStep).pristine;
    }

    /**
     * Returns the form group as FormGroup.
     * @param group
     */
    public getFormGroup(group: any): FormArray {
        return this.formGroup.get(group) as FormArray;
    }

    /**
     * Get selected roles as a string.
     */
    public getSelectedRolesAsString(): string {
        const roles: any = this.formGroup.get('step2.roles').getRawValue();
        const selectedRoles: any = roles.filter((item: any) => item.value);
        const names: string[] = selectedRoles.map((item: { name: any; }) => item.name).toString();

        return this.utilsService.replaceLastCommaWithAnd(names.toString());
    }

    /**
     * Get selected user count.
     */
    public getSelectedUserCount(): number {
        const users: any = this.formGroup.get('step3.users').getRawValue();

        if (users?.length) {
            return users.filter((item: any): any => item.value).length;
        }
    }

    /**
     * Get selected group counter.
     * @param groupFriendlyName
     */
    public getCounterByGroup(groupFriendlyName: string): number {
        const userValues: any = this.formGroup.get('step3.users').getRawValue();

        return userValues.filter(
            (item: { letterGroup: string; value: any; }) => item.letterGroup === groupFriendlyName && item.value
        ).length;
    }

    /**
     * Submit helper for emitter.
     */
    public submitHelper(): void {
        const formValues = this.formGroup.getRawValue();
        const groupValues = {
            id: formValues.step1?.id,
            name: formValues.step1?.name,
            roles: this.formatSection(formValues.step2?.roles),
            users: this.formatSection(formValues.step3?.users)
        };
        this.submitGroup.emit(groupValues);
    }

    /**
     * Set accordion state.
     * @param activeStep
     * @private
     */
    private setAccordion(activeStep: number): void {
        if (activeStep === 3) {
            this.forceCollapseAll = false;
            this.enableToggleOfAccordions = true;
        }

        if (activeStep === 4) {
            this.forceCollapseAll = true;
            this.expandAll = false;
            this.enableToggleOfAccordions = false;
        }
    }

    /**
     * Format form section.
     * @param formValues
     * @private
     */
    private formatSection(formValues: any[]): any[] {
        const filteredValues = formValues.filter(item => item.value);

        return filteredValues.map(item => item.id.toString());
    }

    /**
     * Set up the form builder.
     * @private
     */
    private setupFormGroup(): void {
        // Define our form builder.
        this.formGroup = this.formBuilder.group({
            step1: this.formBuilder.group({
                id: [null], // used for edit
                name: [null, [Validators.required, Validators.maxLength(254), nameValidation]],
            }),
            step2: this.formBuilder.group({
                roles: this.formBuilder.array([])
            }),
            step3: this.formBuilder.group({
                users: this.formBuilder.array([])
            })
        });
    }

    /**
     * Set up form subscribers.
     * @private
     */
    private setUpFormSubscribers(): void {
        this.formGroup.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (values: any): void => {
                    if (JSON.stringify(this.initialFormData) === JSON.stringify(values)) {
                        this.formGroup.markAsPristine();
                    } else {
                        if (this.editId) {
                            this.formGroup.get('step2.roles').markAsDirty();
                            this.formGroup.get('step3.users').markAsDirty();
                        } else {
                            this.formGroup.markAsDirty();
                        }
                    }
                }
            });

        let storedNameValue = '';
        const nameField = this.formGroup.get('step1.name');
        nameField.valueChanges
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (value: any): void => {
                    this.utilsService.debounce('step1', () => {
                        if (value !== storedNameValue && !this.editId) {
                            storedNameValue = value;
                            const existingGroupNames = this.data?.groups.map((item: any) => item.name.toLowerCase());
                            if (!nameField.hasError('duplicateGroupName') && existingGroupNames.includes(value.toLowerCase())) {
                                nameField.setErrors({
                                    'duplicateGroupName': `${this.languageService.translate('duplicate_name')}`
                                });
                                nameField.markAsTouched();
                                this.formGroup.get('step1').updateValueAndValidity();
                            }
                        }
                    });
                }
            });
    }

    /**
     * Create form groups for each step.
     * @param data
     * @private
     */
    private createFormGroups(data: any): void {
        for (const role of data?.roles) {
            // TODO backend should not return admin id:1
            if (role.id !== 1) {
                this.addRoles(role);
            }
        }

        const users = this.utilsService.sortArrayByProperty(data?.users, 'firstName');
        for (const user of users) {
            const letterGroup: string = this.getLetterGroup(user.firstName?.charAt(0));
            this.addUsers(user, letterGroup);
        }

        for (const group of this.grouping) {
            this.filteredFormGroups[group.friendlyName] = this.filterEndpointsByGroup(group.letters);
        }
    }

    /**
     * Get letter group.
     * @param firstLetter
     * @private
     */
    private getLetterGroup(firstLetter: string): string {
        const match = this.grouping.find(item => item.letters.includes(firstLetter?.toLowerCase()));
        if (match) {
            return match.friendlyName;
        }
    }

    /**
     * Filer endpoint by group.
     * @param letters
     * @private
     */
    private filterEndpointsByGroup(letters: string[]): any[] {
        return this.formUsers.controls.filter(
            (control: AbstractControl) => {
                const firstLetter = control.value.firstName.charAt(0);

                return letters.includes(firstLetter.toLowerCase());
            }
        );
    }

    /**
     * Add the new acl rule to the acl rules array.
     * @param role
     * @private
     */
    private addRoles(role: any): void {
        this.formRoles.push(
            this.formBuilder.group(
                {
                    id: role.id,
                    name: role.name,
                    value: role.value || false
                }
            )
        );
    }

    /**
     * Add the new acl rule to the acl rules array.
     * @param user
     * @param letterGroup
     * @private
     */
    private addUsers(user: any, letterGroup: string): void {
        this.formUsers.push(
            this.formBuilder.group(
                {
                    id: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    email: user.email,
                    letterGroup,
                    value: user.value || false
                }
            )
        );
    }

    /**
     * Patch values to form.
     * @param editGroup
     * @private
     */
    private patchValuesToForm(editGroup: any): void {
        const formValues = this.formGroup.value;

        const patch = {
            step1: { id: editGroup.id, name: editGroup.name, },
            step2: { roles: this.updateValueInArray(formValues.step2.roles, editGroup.roles) },
            step3: { users: this.updateValueInArray(formValues.step3.users, editGroup.users) }
        };

        this.initialFormData = patch;
        this.formGroup.patchValue(patch);
        this.disableSystemAdminName();
        this.disableSystemAdminRole();
        this.disableSystemAdminUser();
        this.formGroup.updateValueAndValidity();
    }

    /**
     * Set form array values.
     * @param formValues
     * @param valuesArray
     * @private
     */
    private updateValueInArray(formValues: any, valuesArray: any[]): any {
        if (valuesArray?.length) {
            for (const item of formValues) {
                if (valuesArray.includes(item.id.toString())) {
                    item.value = true;
                }
            }
        }

        return formValues;
    }

    /**
     * Disable system admin name.
     * @private
     */
    private disableSystemAdminName(): void {
        const nameForm = this.formGroup.get('step1.name');
        if (nameForm.value === 'System Admin') {
            nameForm.disable({ onlySelf: true, emitEvent: false });
        }
    }

    /**
     * Disable system admin.
     * This has to be done after the patch.
     * @private
     */
    private disableSystemAdminRole(): void {
        const rolesForm: FormArray = this.formRoles;
        const disableIndex: number = rolesForm.value.findIndex(
            (item: any): boolean => item.name === 'System Admin'
        );
        if (disableIndex !== -1) {
            // @ts-ignore
            const controls: AbstractControl = rolesForm.controls;
            controls[disableIndex].disable({ onlySelf: true, emitEvent: false });
        }
    }

    /**
     * Disable system admin.
     * This has to be done after the patch.
     * @private
     */
    private disableSystemAdminUser(): void {
        const usersForm: FormArray = this.formUsers;
        const disableIndex: number = usersForm.value.findIndex(
            (item: any): boolean => item.firstName + ' ' + item.lastName === 'System Admin'
        );
        if (disableIndex !== -1) {
            // @ts-ignore
            const controls: AbstractControl = usersForm.controls;
            controls[disableIndex].disable({ onlySelf: true, emitEvent: false });
        }
    }

    /**
     * Scroll to active step.
     * @private
     */
    private scrollToActiveStep(): void {
        const elements = this.steps.toArray();
        elements[this.activeStep - 1].nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest'
        });
    }
}
